import { z } from "zod";
import {
  ContractType,
  Commodities,
  BaseContractValidation,
  BasePriceReownValidation,
  RangeValidation,
  RangePriceReownValidation,
  RangeWDUValidation,
  ReOwnWDUValidation,
  MinMaxValidation,
  HedgePlusValidation,
  PremiumValidation,
  DoubleUpValidation,
  MinPriceValidation,
  MinimumPriceValidation,
  AccumulatorValidation,
  MinMaxDoubleUpValidation,
} from "@/lib/api/otc";
import {
  isReownContractType,
  parseContractRefGroups,
  validateContractRefGroups,
} from "./contract-reference-utils";

// ---------------------------------------------------------------------------
// Form-level extension of the API base schema.
//
// Adds fields that exist on the form but are missing from the API schemas:
//   - Adjusted rows 2 & 3
//   - Derived/computed fields (margin, margin_amount, etc.)
//   - superRefine for cross-field rules (canola implied_fx_amount)
//
// The API schemas stay untouched.  These form schemas are the complete
// source of truth for the OTC form: visibility, required, validation,
// and field stripping on submit.
// ---------------------------------------------------------------------------

/**
 * Extra fields shared by ALL contract types (added to the base).
 */
const formExtraFields = {
  // Adjusted row 2
  adj_option_premium_two: z.string().optional(),
  adj_contract_fee_two: z.string().optional(),
  adj_margin_two: z.string().optional(),
  adj_margin_amount_two: z.string().optional(),
  adjusted_date_two: z.string().optional(),

  // Adjusted row 3
  adj_option_premium_three: z.string().optional(),
  adj_contract_fee_three: z.string().optional(),
  adj_margin_three: z.string().optional(),
  adj_margin_amount_three: z.string().optional(),
  adjusted_date_three: z.string().optional(),
};

/**
 * Shared cross-field refinements applied to all form schemas.
 *
 * IMPORTANT: These rules enforce validation at submit time, but they do NOT
 * affect the "Required" label in the UI (which is derived from the static
 * Zod schema shape).  For any field whose required status is conditional
 * (i.e. enforced here via superRefine rather than the schema shape), you
 * must ALSO set `requiredOverride` in the field-meta (see field-meta.tsx)
 * so the UI label stays in sync with the validation rule.
 */
function sharedSuperRefine(data: Record<string, unknown>, ctx: z.RefinementCtx) {
  // implied_fx_amount is required when commodity is Canola.
  // UI "Required" label is controlled by requiredOverride in field-meta.tsx.
  if (
    (data.commodity as string)?.toLocaleUpperCase() === Commodities.Canola &&
    (!data.implied_fx_amount || (data.implied_fx_amount as string).trim() === "")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Required",
      path: ["implied_fx_amount"],
    });
  }

  // Reown contract reference: validate that all groups are complete.
  if (isReownContractType(data.contract_type as string | undefined)) {
    const groups = parseContractRefGroups(data.contract_reference as string | undefined);
    const groupErrors = validateContractRefGroups(groups);
    if (groupErrors !== null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Incomplete contract reference groups",
        path: ["contract_reference"],
      });
    }
  }

  // shipment_end_date must be after shipment_start_date
  if (data.shipment_start_date && data.shipment_end_date) {
    const start = new Date(data.shipment_start_date as string);
    const end = new Date(data.shipment_end_date as string);
    if (!isNaN(start.getTime()) && !isNaN(end.getTime()) && end <= start) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date must be after start date",
        path: ["shipment_end_date"],
      });
    }
  }
}

/**
 * The form-level base schema: API base + extra fields + cross-field refines.
 */
const FormBaseValidation = BaseContractValidation
  .extend(formExtraFields)
  .superRefine(sharedSuperRefine);

// ---------------------------------------------------------------------------
// Helper: extend a variant schema with the form extras + canola refine.
//
// Each API variant schema already extends BaseContractValidation.  We
// re-extend it with the form extra fields and apply the same superRefine.
// ---------------------------------------------------------------------------

function formVariant(
  apiSchema: z.ZodObject<z.ZodRawShape>,
): z.ZodEffects<z.ZodObject<z.ZodRawShape>> {
  return apiSchema.extend(formExtraFields).superRefine(sharedSuperRefine);
}

// ---------------------------------------------------------------------------
// Form variant schemas – one per contract type
// ---------------------------------------------------------------------------

export const FormBasePriceReownValidation = formVariant(BasePriceReownValidation as z.ZodObject<z.ZodRawShape>);
export const FormRangeValidation = formVariant(RangeValidation as z.ZodObject<z.ZodRawShape>);
export const FormRangePriceReownValidation = formVariant(RangePriceReownValidation as z.ZodObject<z.ZodRawShape>);
export const FormRangeWDUValidation = formVariant(RangeWDUValidation as z.ZodObject<z.ZodRawShape>);
export const FormReOwnWDUValidation = formVariant(ReOwnWDUValidation as z.ZodObject<z.ZodRawShape>);
export const FormMinMaxValidation = formVariant(MinMaxValidation as z.ZodObject<z.ZodRawShape>);
export const FormHedgePlusValidation = formVariant(HedgePlusValidation as z.ZodObject<z.ZodRawShape>);
export const FormPremiumValidation = formVariant(PremiumValidation as z.ZodObject<z.ZodRawShape>);
export const FormDoubleUpValidation = formVariant(DoubleUpValidation as z.ZodObject<z.ZodRawShape>);
export const FormMinPriceValidation = formVariant(MinPriceValidation as z.ZodObject<z.ZodRawShape>);
export const FormMinimumPriceValidation = formVariant(MinimumPriceValidation as z.ZodObject<z.ZodRawShape>);
export const FormAccumulatorValidation = formVariant(AccumulatorValidation as z.ZodObject<z.ZodRawShape>);
export const FormMinMaxDoubleUpValidation = formVariant(MinMaxDoubleUpValidation as z.ZodObject<z.ZodRawShape>);

// ---------------------------------------------------------------------------
// The form base (used by SchemaConfig for field introspection).
// This is the ZodObject *before* superRefine so the form library can
// inspect .shape for field names, required status, etc.
// ---------------------------------------------------------------------------

export const FormBaseShape = BaseContractValidation.extend(formExtraFields);
